import styled from '@emotion/styled';
import { Text } from 'components/text/Text';
import { colors } from 'styles/theme/colors';

// Spacing tokens read from the active theme when it carries the app shape
// (Emotion `styledTheme` with `.s`), with static fallbacks under a bare MUI
// theme where `.s` is absent — `styles/theme/theme` must not be imported here
// (it participates in an import cycle at module-eval time).
type AppSpacingTheme = { s?: Partial<Record<'xxxs' | 'xxs' | 'xs', string>> };
const sToken = (theme: AppSpacingTheme, key: 'xxxs' | 'xxs' | 'xs', fallback: string) =>
  theme.s?.[key] ?? fallback;

const hideScrollbars = {
  scrollbarWidth: 'none' as const,
  msOverflowStyle: 'none' as const,
  '&::-webkit-scrollbar': {
    display: 'none',
    width: 0,
    height: 0,
  },
};

/**
 * Fills the host panel so the viewer can measure width and scale one page to fit.
 * `position: relative` anchors the floating pager toolbar.
 */
export const PdfViewerRoot = styled('div')({
  position: 'relative',
  flex: '1 1 0%',
  minWidth: 0,
  width: '100%',
  minHeight: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  alignSelf: 'stretch',
  overflow: 'hidden',
  boxSizing: 'border-box',
});

/** Width-fitted page; vertical scroll when the page is taller than the panel. */
export const PdfCanvasClip = styled('div')({
  flex: '1 1 0%',
  minHeight: 0,
  width: '100%',
  height: 'auto',
  alignSelf: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  /** Keep bitmap-sized canvas from being stretched horizontally by flex. */
  alignItems: 'center',
  overflowX: 'hidden',
  overflowY: 'auto',
  ...hideScrollbars,
});

export const PreviewCanvas = styled('canvas')({
  display: 'block',
  maxWidth: '100%',
  width: 'auto',
  height: 'auto',
  flexShrink: 0,
  backgroundColor: colors.white,
});

export const PdfViewerCentered = styled('div')({
  flex: '1 1 0%',
  minHeight: 160,
  width: '100%',
  alignSelf: 'stretch',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
});

export const PreviewToolbarFloating = styled('div')(p => ({
  position: 'absolute',
  left: '50%',
  bottom: sToken(p.theme, 'xxs', '4px'),
  transform: 'translateX(-50%)',
  pointerEvents: 'auto',
  display: 'flex',
  alignItems: 'center',
  gap: sToken(p.theme, 'xs', '8px'),
  padding: `${sToken(p.theme, 'xxs', '4px')} ${sToken(p.theme, 'xs', '8px')}`,
  paddingLeft: sToken(p.theme, 'xs', '8px'),
  paddingRight: sToken(p.theme, 'xxxs', '2px'),
  borderRadius: '5px',
  backgroundColor: 'rgba(51, 51, 51, 0.92)',
  boxShadow: '0 4px 14px rgba(25, 25, 27, 0.2)',
  zIndex: 2,
  maxWidth: 'calc(100% - 24px)',
  flexWrap: 'wrap',
  justifyContent: 'center',
  '& .MuiIconButton-root': {
    color: colors.grey[0],
    '&.Mui-disabled': {
      color: colors.grey[600],
    },
  },
}));

export const PageNumberField = styled('input')({
  width: 32,
  height: 24,
  borderRadius: 4,
  border: 'none',
  backgroundColor: colors.grey[700],
  color: colors.grey[0],
  textAlign: 'center',
  fontSize: 12,
  fontWeight: 600,
  fontFamily: 'inherit',
  padding: 0,
  flexShrink: 0,
  MozAppearance: 'textfield',
  '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '&:focus': {
    outline: 'none',
    boxShadow: `0 0 0 1px ${colors.grey[400]}`,
  },
  '&:disabled': {
    opacity: 0.85,
    cursor: 'default',
  },
});

export const PageNavFraction = styled('div')(p => ({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  paddingLeft: sToken(p.theme, 'xxxs', '2px'),
  paddingRight: sToken(p.theme, 'xxxs', '2px'),
}));

export const OfWord = styled('span')({
  color: colors.grey[0],
  fontWeight: 500,
  fontSize: 13,
  lineHeight: 1,
  userSelect: 'none',
  paddingLeft: 2,
  paddingRight: 2,
});

export const TotalPagesLabel = styled(Text)({
  color: colors.grey[0],
  fontWeight: 600,
  fontSize: 13,
  minWidth: 16,
  textAlign: 'left',
  fontVariantNumeric: 'tabular-nums',
});
